import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";

const CATEGORIES = ["Excavator", "Bulldozer", "Wheel Loader", "Skid Steer", "Dump Truck", "Crane", "Grader", "Backhoe", "Other"];
const CONDITIONS = ["New", "Used", "Refurbished"];

const EMPTY = {
  title: "",
  category: "Excavator",
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  hours: 0,
  price: 0,
  condition: "Used",
  location: "Houston, TX",
  vin: "",
  description: "",
  images: [],
  specs: {},
  featured: false,
  sold: false,
};

export const EquipmentForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [newImg, setNewImg] = useState("");
  const [specKey, setSpecKey] = useState("");
  const [specVal, setSpecVal] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addImage = () => {
    if (!newImg.trim()) return;
    set("images", [...form.images, newImg.trim()]);
    setNewImg("");
  };
  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const addSpec = () => {
    if (!specKey.trim()) return;
    set("specs", { ...form.specs, [specKey]: specVal });
    setSpecKey("");
    setSpecVal("");
  };
  const removeSpec = (k) => {
    const { [k]: _, ...rest } = form.specs;
    set("specs", rest);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.brand || !form.model) {
      toast.error("Title, brand and model are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        hours: Number(form.hours),
        price: Number(form.price),
      };
      if (initial?.id) {
        await api.put(`/equipment/${initial.id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/equipment", payload);
        toast.success("Listing created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="equipment-form-modal" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
      <div className="mt-10 w-full max-w-4xl border border-[#27272A] bg-[#141415]">
        <div className="sticky top-0 flex items-center justify-between border-b border-[#27272A] bg-[#141415] p-5">
          <h2 className="font-display text-2xl font-bold uppercase">
            {initial?.id ? "Edit" : "New"} Listing
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#27272A]" data-testid="form-close-btn"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={save} className="space-y-6 p-6">
          <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="form-title" />

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <SelectField label="Category" value={form.category} onChange={(v) => set("category", v)} options={CATEGORIES} testid="form-category" />
            <Field label="Brand *" value={form.brand} onChange={(v) => set("brand", v)} testid="form-brand" />
            <Field label="Model *" value={form.model} onChange={(v) => set("model", v)} testid="form-model" />
            <SelectField label="Condition" value={form.condition} onChange={(v) => set("condition", v)} options={CONDITIONS} testid="form-condition" />
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Field label="Year" type="number" value={form.year} onChange={(v) => set("year", v)} testid="form-year" />
            <Field label="Hours" type="number" value={form.hours} onChange={(v) => set("hours", v)} testid="form-hours" />
            <Field label="Price (USD)" type="number" value={form.price} onChange={(v) => set("price", v)} testid="form-price" />
            <Field label="Location" value={form.location} onChange={(v) => set("location", v)} testid="form-location" />
          </div>

          <Field label="VIN / PIN / Serial" value={form.vin} onChange={(v) => set("vin", v)} testid="form-vin" />

          <div>
            <Label>Description</Label>
            <textarea
              data-testid="form-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={4}
              className="w-full border border-[#27272A] bg-[#0a0a0b] p-3 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
            />
          </div>

          {/* IMAGES */}
          <div>
            <Label>Image URLs</Label>
            <div className="flex gap-2">
              <input
                value={newImg}
                onChange={(e) => setNewImg(e.target.value)}
                placeholder="https://…"
                className="flex-1 border border-[#27272A] bg-[#0a0a0b] p-2 font-mono text-sm focus:border-[#FFB800] focus:outline-none"
              />
              <button type="button" onClick={addImage} className="bg-[#FFB800] px-4 font-display text-xs font-bold uppercase tracking-widest text-black hover:bg-[#E5A600]">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
              {form.images.map((img, i) => (
                <div key={i} className="relative border border-[#27272A]">
                  <img src={img} alt="" className="aspect-video w-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute right-1 top-1 bg-black/80 p-1 hover:bg-red-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* SPECS */}
          <div>
            <Label>Specifications</Label>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <input value={specKey} onChange={(e) => setSpecKey(e.target.value)} placeholder="Label (e.g. Operating Weight)" className="border border-[#27272A] bg-[#0a0a0b] p-2 font-mono text-sm focus:border-[#FFB800] focus:outline-none" />
              <input value={specVal} onChange={(e) => setSpecVal(e.target.value)} placeholder="Value (e.g. 80,600 lbs)" className="border border-[#27272A] bg-[#0a0a0b] p-2 font-mono text-sm focus:border-[#FFB800] focus:outline-none" />
              <button type="button" onClick={addSpec} className="bg-[#FFB800] px-4 font-display text-xs font-bold uppercase text-black hover:bg-[#E5A600]"><Plus className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 grid gap-1 md:grid-cols-2">
              {Object.entries(form.specs).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-[#27272A] bg-[#0a0a0b] px-3 py-2 text-sm">
                  <span><span className="font-mono text-[10px] uppercase text-white/50">{k}:</span> <b>{v}</b></span>
                  <button type="button" onClick={() => removeSpec(k)} className="text-white/50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" className="accent-[#FFB800]" />
              Featured
            </label>
            <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider">
              <input type="checkbox" checked={form.sold} onChange={(e) => set("sold", e.target.checked)} data-testid="form-sold" className="accent-[#FFB800]" />
              Mark Sold
            </label>
          </div>

          <div className="flex gap-3 border-t border-[#27272A] pt-6">
            <button type="submit" disabled={saving} data-testid="form-save-btn" className="bg-[#FFB800] px-8 py-3 font-display text-sm font-bold uppercase tracking-widest text-black hover:bg-[#E5A600] disabled:opacity-50">
              {saving ? "Saving…" : initial?.id ? "Update Listing" : "Create Listing"}
            </button>
            <button type="button" onClick={onClose} className="border border-[#27272A] px-8 py-3 font-display text-sm font-bold uppercase tracking-widest hover:border-white/40">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Label = ({ children }) => <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{children}</label>;

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testid}
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#27272A] bg-[#0a0a0b] p-2.5 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label>{label}</Label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-[#27272A] bg-[#0a0a0b] p-2.5 font-mono text-sm text-white focus:border-[#FFB800] focus:outline-none"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
